import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import * as _ from 'lodash';
import { LifeResourceIdentifier, InterviewDisclosureIdentifier, ConditionDisclosureResource, DisclosurePhysicianResource, PrimaryPhysicianResource, PhysicianResource, ListDefinitionResource, ListItemResource, AisRestService, PhysicianResourceIdentifier, CaseResourceIdentifier, DisclosurePhysicianIdentifier } from '@Components/mras/core';
import { FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { ScreensInterviewService } from '../../screens-interview.service';

@Component({
    selector: 'jhi-physician-capture',
    templateUrl: './physician-capture.component.html',
    styleUrls: ['./physician-capture.component.scss']
})
export class PhysicianCaptureComponent implements OnInit, OnDestroy {
    readonly _ = _; // create assignment so lodash can be used in template

    @Input()
        name: string;

    @Input()
        lifeIdentifier: LifeResourceIdentifier;

    @Input()
        primary: boolean;
    @Input()
        summaryMode: boolean;
    @Input()
        disclosureIdentifier: InterviewDisclosureIdentifier;

    @Input()
        disclosure: ConditionDisclosureResource;
    @Output()
        disclosureChange = new EventEmitter<ConditionDisclosureResource>();

    @Input()
        disclosurePhysicians: DisclosurePhysicianResource[];
    @Output()
        disclosurePhysiciansChange = new EventEmitter<DisclosurePhysicianResource[]>();

    @Input()
        primaryPhysician: PrimaryPhysicianResource;
    @Output()
        primaryPhysicianChange = new EventEmitter<PrimaryPhysicianResource>();

    @Input()
        physicians: PhysicianResource[];
    @Output()
        physiciansChange = new EventEmitter<PhysicianResource[]>();

    
    physiciansValue: PhysicianResource[];
    disclosurePhysiciansValue: DisclosurePhysicianResource[];

    @Input()
        countries: ListDefinitionResource;

    _hasPhysician: boolean;
    set hasPhysician(val: boolean) {
        this._hasPhysician = val;

        if (val === true) {
            this.hasPhysicians = "Yes";
            return;
        }

        if (val === false) {
            this.hasPhysicians = "No";
            return;
        }

        if (!val)  {
            this.hasPhysicians = undefined;
        }
    }
    get hasPhysician() {
        return this._hasPhysician;
    }

    hasPhysicians: string;
    hasPhysicianLabel: string;
    countryStatesMap = new Map<PhysicianResource, ListItemResource[]>();

    physicianForm: UntypedFormGroup;

    constructor(private aisRestService: AisRestService,
            private screensInterviewService: ScreensInterviewService,
        private _fb: UntypedFormBuilder
    ) {}
    ngOnDestroy(): void {
        this.screensInterviewService.removeForm(this.physicianForm);
    }

    get physcianDetails() {
        return this.physicianForm.controls["physcianDetails"] as UntypedFormArray;
    }

    ngOnInit(): void {
        this.physiciansValue = [];

        this.physicianForm = this._fb.group({
            hasPhysicians: ['', Validators.required],
            physcianDetails: this._fb.array([])
        });

        this.screensInterviewService.addForm(this.physicianForm);

        if (this.primary) {
            this.hasPhysicianLabel = 'Do you have a primary care physician or medical center?';
            if (this.primaryPhysician != null) {
                const primaryPhysician = (this.physicians || []).find(e => e.uuid === this.primaryPhysician[0].physicianIdentifier.uuid);
                if (primaryPhysician != null) {
                    this.hasPhysician = true;
                    this.physicianForm.controls['hasPhysicians'].setValue(this.hasPhysicians);
                    this.physiciansValue.push(primaryPhysician);
                    this.fetchStatesForCountry(primaryPhysician);
                }
            }
        } else {
            this.hasPhysicianLabel = `Do you have a primary care physician, or do you go to a health care facility for ${this.disclosure.aliasName}?`;
            this.disclosurePhysiciansValue = (this.disclosure.disclosurePhysicians || [])
                .map(e => {
                    const resource = this.disclosurePhysicians.find(f => f.physicianIdentifier.uuid === e.physicianUuid);
                    const copy = new DisclosurePhysicianResource();
                    return Object.assign(copy, resource);
                })
                .filter(e => e != null);
            this.physiciansValue = (this.disclosure.disclosurePhysicians || [])
                .map(e => this.physicians.find(f => f.uuid === e.physicianUuid))
                .map(e => {
                    const copy = Object.assign(new PhysicianResource(), e);
                    this.fetchStatesForCountry(copy);
                    return copy;
                })
                .filter(e => e != null);
            this.hasPhysician = this.disclosure.hasPhysicians;
            this.physicianForm.controls['hasPhysicians'].setValue(this.hasPhysicians);
            if (this.hasPhysician !== false && this.physiciansValue.length === 0) {
                this.hasPhysician = undefined;
                this.physicianForm.controls['hasPhysicians'].setValue(this.hasPhysicians);
            }
        }

        const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');

        for (const physicianValue of this.physiciansValue) {
            formArray.push(this.createItem(physicianValue));
        }
    }

    createItem(physicianValue: PhysicianResource): UntypedFormGroup {

        const resource = this.disclosurePhysicians.find(f => f.physicianIdentifier.uuid === physicianValue.uuid);
        const fg = this._fb.group({
            id: [physicianValue.uuid],
            name: [physicianValue.name],
            speciality: [physicianValue.speciality],
            addressLine1: [physicianValue.addressLine1],
            addressLine2: [physicianValue.addressLine2],
            countryCode: [physicianValue.country.code],
            stateString: [physicianValue.stateString],
            city: [physicianValue.city],
            zip: [physicianValue.zip],
            phone: [physicianValue.phone],
            email: [physicianValue.email],
            fax: [physicianValue.fax],
            lastVisitDateExplicitlyUnknown: [resource?.lastVisitDateExplicitlyUnknown]
        });

        if (resource?.lastVisitDateExplicitlyUnknown === true) {
            fg.addControl('lastVisitMonth', new UntypedFormControl(resource?.lastVisitMonth));
            fg.addControl('lastVisitYear', new UntypedFormControl(resource?.lastVisitYear));
        }else{            
            fg.addControl('lastVisitMonth', new UntypedFormControl(resource?.lastVisitMonth, Validators.required));
            fg.addControl('lastVisitYear', new UntypedFormControl(resource?.lastVisitYear, Validators.required));
        }

        fg.markAsPristine();

        return fg;
    }

    mapPhysician(idx: number): PhysicianResource {
        const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
        
        const physicianDetail = formArray.controls[idx] as UntypedFormGroup;
        const disclosure = this.disclosurePhysiciansValue[idx];

        disclosure.lastVisitMonth = physicianDetail.controls["lastVisitMonth"].value;        
        disclosure.lastVisitYear = physicianDetail.controls["lastVisitYear"].value;      
        disclosure.lastVisitDateExplicitlyUnknown = physicianDetail.controls["lastVisitDateExplicitlyUnknown"].value;

        if (disclosure.lastVisitDateExplicitlyUnknown === true) {
            disclosure.lastVisitMonth = 0;
            disclosure.lastVisitYear = 0;
        }

        if (physicianDetail.controls["id"].value !== null || physicianDetail.controls["id"].value !== '') {
            const physician = this.physiciansValue[idx];
            
            physician.name= physicianDetail.controls["name"].value;
            physician.speciality= physicianDetail.controls["speciality"].value;
            physician.addressLine1= physicianDetail.controls["addressLine1"].value;
            physician.addressLine2= physicianDetail.controls["addressLine2"].value;
            physician.country= {
                code: physicianDetail.controls["countryCode"].value,
                description: physicianDetail.controls["countryCode"].value,
            };
            physician.stateString= physicianDetail.controls["stateString"].value;
            physician.city= physicianDetail.controls["city"].value;
            physician.zip= physicianDetail.controls["zip"].value;
            physician.phone= physicianDetail.controls["phone"].value;
            physician.email= physicianDetail.controls["email"].value;
            physician.fax= physicianDetail.controls["fax"].value;

            return physician;
        }else{
            return {
                uuid: physicianDetail.controls["id"].value,
                name: physicianDetail.controls["name"].value,
                speciality: physicianDetail.controls["speciality"].value,
                addressLine1: physicianDetail.controls["addressLine1"].value,
                addressLine2: physicianDetail.controls["addressLine2"].value,
                country: {
                    code: physicianDetail.controls["countryCode"].value,
                    description: physicianDetail.controls["countryCode"].value,
                },
                stateString: physicianDetail.controls["stateString"].value,
                city: physicianDetail.controls["city"].value,
                zip: physicianDetail.controls["zip"].value,
                phone: physicianDetail.controls["phone"].value,
                email: physicianDetail.controls["email"].value,
                fax: physicianDetail.controls["fax"].value,
                _embedded: null,
                _links: null,
                links: null,
                source: null,
                state: null
            };
        }
    }

    submit($index: number) {
        const physicianResource = this.mapPhysician($index);
        let caseId;
        if (this.primary) {
            caseId = this.lifeIdentifier.caseId;
        } else {
            caseId = this.disclosureIdentifier.caseId;
        }

        let physician$: Observable<PhysicianResource>;
        const isUpdate = physicianResource.uuid != null;
        if (isUpdate) {
            const identifier = new PhysicianResourceIdentifier();
            identifier.caseId = caseId;
            identifier.uuid = physicianResource.uuid;
            physician$ = this.aisRestService.updatePhysician(identifier, physicianResource);
        } else {
            const identifier = new CaseResourceIdentifier();
            identifier.caseId = caseId;
            physician$ = this.aisRestService.createPhysician(identifier, physicianResource);
        }

        physician$
            .pipe(
                tap(e => {
                    if (this.primary) {
                        this.physiciansValue[0].uuid = e.uuid;
                    } else {
                        this.physiciansValue[$index].uuid = e.uuid;
                    }
                    if (this.physicians) {
                        this.physicians.push(e);
                        this.physiciansChange.emit(this.physicians);
                    }
                }),
                map(e => {
                    const identifier = new PhysicianResourceIdentifier();
                    identifier.uuid = e.uuid;
                    identifier.caseId = caseId;
                    
                    const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
                    const physicianDetail = formArray.controls[$index] as UntypedFormGroup;

                    physicianDetail.controls['id'].setValue(identifier.uuid);

                    return identifier;
                })
            )
            .subscribe(physicianIdentifier => {
                if (this.primary) {
                    const primaryPhysicianResource = new PrimaryPhysicianResource();
                    primaryPhysicianResource.physicianExpected = this.hasPhysician;
                    primaryPhysicianResource.physicianIdentifier = physicianIdentifier;
                    primaryPhysicianResource.lastVisitDateExplicitlyUnknown = true;
                    if (isUpdate) {
                        this.aisRestService.updatePrimaryPhysician(this.lifeIdentifier, primaryPhysicianResource).subscribe();
                    } else {
                        this.aisRestService.createPrimaryPhysician(this.lifeIdentifier, primaryPhysicianResource).subscribe();
                    }
                } else {
                    const resource = this.disclosurePhysiciansValue[$index];
                    resource.physicianIdentifier = physicianIdentifier;
                    if (isUpdate) {
                        const disclosurePhysicianIdentifier = Object.assign(new DisclosurePhysicianIdentifier(), this.disclosureIdentifier);
                        disclosurePhysicianIdentifier.physicianUuid = resource.physicianIdentifier.uuid;



                        this.aisRestService.updateDisclosurePhysician(disclosurePhysicianIdentifier, resource).subscribe(ignoredResp => {
                            this.aisRestService.getDisclosure(this.disclosureIdentifier).subscribe(getDisclosureResp => {
                                Object.keys(this.disclosure).forEach(k => delete this.disclosure[k]);
                                Object.assign(this.disclosure, getDisclosureResp);
                                this.disclosureChange.emit(this.disclosure);

                                const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
                                        
                                const physicianDetail = formArray.controls[$index] as UntypedFormGroup;
                                this.physicianForm.markAsUntouched();
                                this.physicianForm.markAsPristine();

                            });
                        });
                    } else {
                        this.aisRestService.createDisclosurePhysician(this.disclosureIdentifier, resource).subscribe(ignoredResp => {
                            this.aisRestService.getDisclosure(this.disclosureIdentifier).subscribe(getDisclosureResp => {
                                Object.keys(this.disclosure).forEach(k => delete this.disclosure[k]);
                                Object.assign(this.disclosure, getDisclosureResp);
                                this.disclosureChange.emit(this.disclosure);

                                const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
                                        
                                const physicianDetail = formArray.controls[$index] as UntypedFormGroup;
                                this.physicianForm.markAsUntouched();
                                this.physicianForm.markAsPristine();
                            });
                        });
                    }
                }
            });
    }

    clear($index: number) {
        if (this.physiciansValue) {
            const uuid = this.physiciansValue[$index].uuid;
            Object.keys(this.physiciansValue[$index]).forEach(k => (this.physiciansValue[$index][k] = null));
            this.physiciansValue[$index].country = new ListItemResource();
            this.physiciansValue[$index].state = new ListItemResource();
            this.physiciansValue[$index].uuid = uuid;

            const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
            formArray.controls[$index].reset();

        }
        if (this.disclosurePhysiciansValue) {
            Object.keys(this.disclosurePhysiciansValue[$index]).forEach(k => (this.disclosurePhysiciansValue[$index][k] = null));
        }
    }


    remove($index: number) {
        if (this.physiciansValue) {

            if ($index === 0 && this.physiciansValue.length === 1 && this.hasPhysician === true) {
                this.clear($index);
                return;
            }

            let caseId;
            if (this.primary) {
                caseId = this.lifeIdentifier.caseId;
            } else {                
                caseId = this.disclosureIdentifier.caseId;
            }

            const physicianResource = this.physiciansValue[$index];
            const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');

            if (physicianResource.uuid) {            
                const identifier = new PhysicianResourceIdentifier();
                identifier.caseId = caseId;
                identifier.uuid = physicianResource.uuid;
                const physicianDisclosureIdentifier = this.disclosure.disclosurePhysicians[$index];

                this.aisRestService.deleteDisclosurePhysician(physicianDisclosureIdentifier).subscribe(() => {});
            }

            this.physiciansValue.splice($index,1);
            formArray.controls.splice($index,1);        

            formArray.updateValueAndValidity();

            if (formArray.controls.length === 0) {
                this.hasPhysician = undefined;
            }
        }
        if (this.disclosurePhysiciansValue) {
            Object.keys(this.disclosurePhysiciansValue[$index]).forEach(k => (this.disclosurePhysiciansValue[$index][k] = null));
        }
    }

    onRadioChange(event: MatRadioChange) {
        if (event.value.toString() === "No") {
            this.hasPhysicianAnswer(false);
        }
        if (event.value.toString() === "Yes") {
            this.hasPhysicianAnswer(true);
        }
    }

    onUnknownChange(event, idx) {
        const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');        
        const physicianDetail = formArray.controls[idx] as UntypedFormGroup;        

        if (event.checked) {
            physicianDetail.controls['lastVisitMonth'].clearValidators();
            physicianDetail.controls['lastVisitYear'].clearValidators();

            physicianDetail.controls['lastVisitMonth'].setValue('');
            physicianDetail.controls['lastVisitYear'].setValue('');

            physicianDetail.controls['lastVisitMonth'].updateValueAndValidity();
            physicianDetail.controls['lastVisitYear'].updateValueAndValidity();
        }else{
            physicianDetail.controls['lastVisitMonth'].addValidators(Validators.required);
            physicianDetail.controls['lastVisitYear'].addValidators(Validators.required);
            
            physicianDetail.controls['lastVisitMonth'].updateValueAndValidity();
            physicianDetail.controls['lastVisitYear'].updateValueAndValidity();
        }
    }

    hasPhysicianAnswer(val: boolean) {
        this.hasPhysician = val;
        
        if (this.primary && !this.hasPhysician && this.primaryPhysician != null) {
            this.aisRestService.deletePrimaryPhysician(this.lifeIdentifier).subscribe(e => {
                this.primaryPhysician = null;
                this.physiciansValue[0].uuid = null;
                this.primaryPhysicianChange.emit(null);
            });
            return;
        }

        if (!this.primary) {
            const resource = new ConditionDisclosureResource();
            resource.hasPhysicians = this.hasPhysician;
            this.aisRestService.updateDisclosure(this.disclosureIdentifier, resource).subscribe(e => {
                this.hasPhysician = e.hasPhysicians;
                this.physicianForm.controls['hasPhysicians'].setValue(this.hasPhysicians);
            });
        }

        if (this.hasPhysician) {
            const physicianResource = new PhysicianResource();
            physicianResource.country = new ListItemResource();
            this.physiciansValue.push(physicianResource);
            const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
            formArray.push(this.createItem(physicianResource));

            if (!this.primary) {
                this.disclosurePhysiciansValue.push(new DisclosurePhysicianResource());
            }

            this.disclosure.physiciansValid = false;
        }

        if (this.hasPhysician === false) {
            let cnt = this.physiciansValue.length;

            while (cnt > 0) {
                cnt--;
                this.remove(cnt);
            }            
        }
    }

    getPhysicians() {
        if (this.physiciansValue.length === 0) {
            const physicianResource = new PhysicianResource();
            physicianResource.country = new ListItemResource();
            this.physiciansValue.push(physicianResource);
        }
        if (!this.primary && this.disclosurePhysiciansValue.length === 0) {
            this.disclosurePhysiciansValue.push(new DisclosurePhysicianResource());
        }
        return this.physiciansValue;
    }

    mapToOptionValueFromListItems(resource: ListItemResource): string {
        return resource.code;
    }

    mapToOptionLabelFromListItems(resource: ListItemResource): string {
        return resource.description;
    }

    fetchStatesForCountry(physician: PhysicianResource) {
        this.aisRestService.getStatesForCountry(this.lifeIdentifier, physician.country.code).subscribe(e => {
            physician.country.description = null;
            this.countryStatesMap.set(physician, e);
            if (e.length === 0) {
                physician.state = null;
            }
            if (e.length > 0 && physician.state == null) {
                physician.state = new ListItemResource();
                physician.stateString = null;
            }
        });
    }
}
